/**
 * Interface defining the methods for Runtime Bridge Clients.
 * Implementations of this interface provide platform-specific
 * mechanisms for communication between the frontend and the runtime environment.
 */
export interface IRuntimeBridgeClient {
    /**
     * Uploads a response file.
     * @param filename - Name of the file to upload.
     * @param apiUrl - API URL to use for the response upload.
     * @param fileData - Optional file data blob to upload directly without retrieving from cache.
     * @param deleteTemporaryResponseDataOnSuccess - Optional flag to delete temporary response data store after successful upload.
     */
    uploadResponse(filename: string, apiUrl: string, fileData?: Blob, deleteTemporaryResponseDataOnSuccess?: boolean, recoveryEntryId?: number): void;

    /**
     * Quits the application.
     */
    quit(): void;

    /**
     * Checks if the application is online.
     * @returns True if online, false otherwise.
     */
    isOnline(): boolean;

    /**
     * Checks if the application is running on Windows.
     * @returns True if running on Windows, false otherwise.
     */
    isWindows(): boolean;

    /**
     * Gets the media URL.
     * @returns Media URL as a string.
     */
    getMediaUrl(): string;

    /**
     * Lists the available keyboard languages.
     * @returns An array of available keyboard language codes.
     */
    listKeyboardLanguagesAvailable(): string[];

    /**
     * Sets the runtime language.
     * @param lang - Language code to set as the runtime language.
     */
    setRuntimeLanguage(lang: string): void;

    /**
     * Notifies that the frontend is ready.
     */
    frontendReady(): void;

    /**
     * Changes the keyboard language.
     * @param lang - Language code to change the keyboard to.
     */
    changeKeyboardLanguage(lang: string): void;
}
